/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { useMutation } from 'react-query'

import type { QueryFunctionReturnType } from '@modules/query/types'
import { invalidateGetBudgets } from '@modules/billing-lib/queries/useGetBudgets'
import { deleteBudgetV1 } from '@modules/billing-api/v1/callers'
import type { operations } from '@modules/billing-api/v1/types'

import type { MutateOptions } from 'react-query'

type DeleteBudgetVariables = {
  budgetId: operations['deleteBudgetV1']['parameters']['path']['budget_id']
  organizationId: operations['deleteBudgetV1']['parameters']['path']['organization_id']
}

const useDeleteBudget = () => {
  const mutation = useMutation<
    QueryFunctionReturnType<typeof deleteBudgetV1>,
    unknown,
    DeleteBudgetVariables
  >({
    mutationFn: ({ budgetId, organizationId }) =>
      deleteBudgetV1({
        pathParameters: { budget_id: budgetId, organization_id: organizationId },
      }),
  })

  /* 
    We need this implementation because invalidateGetBudgets unmounts the components  
    that consume this hook when it’s invoked, affecting the expected behavior
  */
  const mutate = (
    vars: DeleteBudgetVariables,
    opts: MutateOptions<void, unknown, DeleteBudgetVariables, unknown> | undefined,
  ) => {
    mutation.mutate(vars, {
      ...opts,
      onSuccess: (...args) => {
        invalidateGetBudgets(vars.organizationId)
        opts?.onSuccess?.(...args)
      },
    })
  }

  return { ...mutation, mutate }
}

export default useDeleteBudget
